import { CalendarDate } from '@internationalized/date';
import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';
import React from 'react';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateValue,
  Heading,
} from 'react-aria-components';
import { cn } from '../utils';

// ─── Calendar Popover ────────────────────────────────────────────────────────

const CalendarPopover = React.forwardRef<
  HTMLDivElement,
  {
    value: CalendarDate | undefined;
    onSelect: (date: CalendarDate) => void;
    minValue?: DateValue;
    maxValue?: DateValue;
    calendarFooter?: React.ReactNode;
  }
>(function CalendarPopover({ value, onSelect, minValue, maxValue, calendarFooter }, ref) {
  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="bg-white border border-neutral-5 rounded-lg shadow-lg p-3 animate-in fade-in-0 zoom-in-95 outline-none"
    >
      <Calendar
        className="w-70 outline-none"
        value={value}
        onChange={onSelect}
        minValue={minValue ? new CalendarDate(minValue.year, minValue.month, minValue.day) : undefined}
        maxValue={maxValue ? new CalendarDate(maxValue.year, maxValue.month, maxValue.day) : undefined}
      >
        <header className="flex items-center justify-between mb-2">
          <Button
            slot="previous"
            excludeFromTabOrder
            className="p-4 rounded-md hover:bg-neutral-2 transition-colors outline-none focus:ring-2 focus:ring-ring-primary cursor-pointer"
          >
            <RiArrowLeftSLine className="h-5 w-5 text-neutral-text" />
          </Button>
          <Heading className="text-14 font-medium text-neutral-text" />
          <Button
            slot="next"
            excludeFromTabOrder
            className="p-4 rounded-md hover:bg-neutral-2 transition-colors outline-none focus:ring-2 focus:ring-ring-primary cursor-pointer"
          >
            <RiArrowRightSLine className="h-5 w-5 text-neutral-text" />
          </Button>
        </header>
        <CalendarGrid className="w-full border-collapse">
          <CalendarGridHeader>
            {(day) => (
              <CalendarHeaderCell className="text-12 font-medium text-neutral-alpha-7 pb-2 w-10">
                {day}
              </CalendarHeaderCell>
            )}
          </CalendarGridHeader>
          <CalendarGridBody>
            {(date) => (
              <CalendarCell
                date={date}
                className={cn(
                  'flex items-center justify-center w-10 h-9 text-14 text-neutral-alpha-9 rounded-md outline-none cursor-pointer',
                  'hover:bg-neutral-2 transition-colors',
                  'focus:ring-2 focus:ring-ring-primary focus:ring-inset',
                  'data-selected:bg-white data-selected:border data-selected:border-brand-6 data-selected:rounded-md data-selected:hover:bg-neutral-2',
                  'data-today:text-brand-text data-today:bg-brand-2 data-today:rounded-md',
                  'data-outside-month:text-neutral-alpha-5',
                  'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
                  'data-unavailable:line-through data-unavailable:text-neutral-alpha-5'
                )}
              />
            )}
          </CalendarGridBody>
        </CalendarGrid>
      </Calendar>
      {calendarFooter}
    </div>
  );
});

export default CalendarPopover;
